/**
 * API Client - High-level API client wrapper
 * HttpClient'ı kullanarak business logic'i uygular
 */

import { 
  IHttpClient, 
  HttpRequestConfig,
  HttpResponse
} from './httpClient.interface';


import { ENDPOINTS } from '../config/endpoints';
import { 
  ApiResponse,
  isApiResponse,
  PaginatedResponse, 
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  BaseQueryParams,
  ID,
  CreateRequest,
  UpdateRequest,
  ExportRequest,
  ExportFormat
} from '../types';

import { API_CONFIG } from '../config/api.config';
import axiosClient from './axiosClient';

/**
 * API Client Options
 */
export interface ApiClientOptions {
  baseURL?: string;
  timeout?: number;
  enableCache?: boolean;
  defaultHeaders?: Record<string, string>;
  retryCount?: number;
  retryDelay?: number;
}

/**
 * Request Options
 */
export interface RequestOptions extends HttpRequestConfig {
  skipAuth?: boolean;
  cacheKey?: string;
  cacheTTL?: number;
  retryable?: boolean;
  timeout?: number;
  skipCache?: boolean;
  skipAuthRefresh?: boolean;
}

/**
 * API Client Interface
 */
export interface IApiClient {
  // Authentication
  login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>>;
  logout(): Promise<ApiResponse<void>>;
  refreshToken(request: RefreshTokenRequest): Promise<ApiResponse<LoginResponse>>;
  getProfile(): Promise<ApiResponse<any>>;
  changePassword(data: any): Promise<ApiResponse<void>>;

  // CRUD Operations
  create<T>(endpoint: string, data: CreateRequest<T>): Promise<ApiResponse<T>>;
  get<T>(endpoint: string, id: ID, options?: RequestOptions): Promise<ApiResponse<T>>;
  getList<T>(endpoint: string, params?: BaseQueryParams, options?: RequestOptions): Promise<PaginatedResponse<T>>;
  update<T>(endpoint: string, data: UpdateRequest<T>): Promise<ApiResponse<T>>;
  delete(endpoint: string, id: ID, options?: RequestOptions): Promise<ApiResponse<void>>;
  bulkDelete(endpoint: string, ids: ID[]): Promise<ApiResponse<void>>;

  // Custom Operations
  customGet<T>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>>;
  customPost<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>>;
  customPut<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>>;
  customPatch<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>>;
  customDelete<T>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>>;

  // Export Operations
  exportData(endpoint: string, format: ExportFormat, filters?: any): Promise<Blob>;
  importData(endpoint: string, file: File): Promise<ApiResponse<any>>;

  // File Operations
  uploadFile(endpoint: string, file: File, metadata?: any): Promise<ApiResponse<any>>;
  downloadFile(endpoint: string, fileId: ID): Promise<Blob>;

  // Utility Methods
  setAuthToken(token: string): void;
  clearAuth(): void;
  setBaseURL(baseURL: string): void;
  cancelRequest(cancelKey: string): void;
  cancelAllRequests(): void;
}

/**
 * API Client Implementation
 */
export class ApiClient implements IApiClient {
  private readonly httpClient: IHttpClient;
  private readonly cache: Map<string, { data: any; expiry: number }> = new Map();
  public baseURL: string;
  public defaultOptions: RequestOptions;

  constructor(httpClient?: IHttpClient, options?: ApiClientOptions) {
    this.httpClient = httpClient || axiosClient;
    this.baseURL = options?.baseURL || API_CONFIG.BASE_URL;
    
    this.defaultOptions = {
      timeout: options?.timeout || API_CONFIG.TIMEOUT,
      headers: options?.defaultHeaders || {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      requiresAuth: true,
      retryCount: options?.retryCount || API_CONFIG.RETRY.COUNT,
      retryDelay: options?.retryDelay || API_CONFIG.RETRY.DELAY
    };
  }

  // ==================== AUTHENTICATION ====================

  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const endpoint = ENDPOINTS.AUTH.LOGIN;
    
    const response = await this.httpClient.postApi<LoginResponse>(endpoint, credentials, {
      ...this.defaultOptions,
      requiresAuth: false,
      cacheKey: undefined // Login'de cache olmaz
    });

    // Login başarılıysa token'ı kaydet
    if (response.success && response.data.accessToken) {
      this.setAuthToken(response.data.accessToken);
    }

    return response;
  }

  async logout(): Promise<ApiResponse<void>> {
    const endpoint = ENDPOINTS.AUTH.LOGOUT;
    
    try {
      const response = await this.httpClient.postApi<void>(endpoint, {}, this.defaultOptions);
      
      // Logout başarılıysa token'ı temizle
      if (response.success) {
        this.clearAuth();
      }
      
      return response;
    } catch (error) {
      // API hatası olsa bile local logout yap
      this.clearAuth();
      throw error;
    }
  }

  async refreshToken(request: RefreshTokenRequest): Promise<ApiResponse<LoginResponse>> {
    const endpoint = ENDPOINTS.AUTH.REFRESH_TOKEN;
    
    const response = await this.httpClient.postApi<LoginResponse>(endpoint, request, {
      ...this.defaultOptions,
      requiresAuth: false,
      skipAuthRefresh: true // Infinite loop'u önlemek için
    });

    // Refresh başarılıysa yeni token'ı kaydet
    if (response.success && response.data.accessToken) {
      this.setAuthToken(response.data.accessToken);
    }

    return response;
  }

  async getProfile(): Promise<ApiResponse<any>> {
    const endpoint = ENDPOINTS.AUTH.PROFILE;
    
    return this.httpClient.getApi<any>(endpoint, {
      ...this.defaultOptions,
      cacheKey: 'user_profile',
      cacheTTL: 5 * 60 * 1000 // 5 dakika
    });
  }

  async changePassword(data: any): Promise<ApiResponse<void>> {
    const endpoint = ENDPOINTS.AUTH.CHANGE_PASSWORD;
    
    return this.httpClient.postApi<void>(endpoint, data, this.defaultOptions);
  }

  // ==================== CRUD OPERATIONS ====================

  async create<T>(endpoint: string, data: CreateRequest<T>): Promise<ApiResponse<T>> {
    return this.httpClient.postApi<T>(endpoint, data, this.defaultOptions);
  }

  async get<T>(endpoint: string, id: ID, options?: RequestOptions): Promise<ApiResponse<T>> {
    const url = `${endpoint}/${id}`;
    const cacheKey = options?.cacheKey || `get_${endpoint}_${id}`;
    
    return this.httpClient.getApi<T>(url, {
      ...this.defaultOptions,
      ...options,
      cacheKey
    });
  }

  async getList<T>(
    endpoint: string, 
    params?: BaseQueryParams, 
    options?: RequestOptions
  ): Promise<PaginatedResponse<T>> {
    const cacheKey = options?.cacheKey || `list_${endpoint}_${JSON.stringify(params || {})}`;
    
    const response = await this.httpClient.get<ApiResponse<T[]>>(endpoint, {
      ...this.defaultOptions,
      ...options,
      params,
      cacheKey
    });

    // Paginated response'u normalize et
    if (response.data && Array.isArray(response.data.data)) {
      return {
        ...response.data,
        data: response.data.data,
        meta: response.data.meta || this.extractPaginationMeta(response)
      };
    }

    throw new Error('Invalid paginated response format');
  }

  async update<T>(endpoint: string, data: UpdateRequest<T>): Promise<ApiResponse<T>> {
    const url = `${endpoint}/${data.id}`;
    
    return this.httpClient.putApi<T>(url, data.data, this.defaultOptions);
  }

  async delete(endpoint: string, id: ID, options?: RequestOptions): Promise<ApiResponse<void>> {
    const url = `${endpoint}/${id}`;
    
    return this.httpClient.deleteApi<void>(url, {
      ...this.defaultOptions,
      ...options
    });
  }

  async bulkDelete(endpoint: string, ids: ID[]): Promise<ApiResponse<void>> {
    return this.httpClient.postApi<void>(`${endpoint}/bulk-delete`, { ids }, this.defaultOptions);
  }

  // ==================== CUSTOM OPERATIONS ====================

  async customPost<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.httpClient.postApi<T>(endpoint, data, {
      ...this.defaultOptions,
      ...options
    });
  }

  async customPut<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.httpClient.putApi<T>(endpoint, data, {
      ...this.defaultOptions,
      ...options
    });
  }

  async customPatch<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.httpClient.patch<T>(endpoint, data, {
      ...this.defaultOptions,
      ...options
    }).then(response => this.wrapResponse<T>(response));
  }

  async customDelete<T>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.httpClient.deleteApi<T>(endpoint, {
      ...this.defaultOptions,
      ...options
    });
  }

  // ==================== EXPORT OPERATIONS ====================

  async exportData(endpoint: string, format: ExportFormat, filters?: any): Promise<Blob> {
    const exportRequest: ExportRequest = {
      format,
      filters,
      includeHeaders: true
    };

    return this.httpClient.post<Blob>(`${endpoint}/export`, exportRequest, {
      ...this.defaultOptions,
      responseType: 'blob',
      headers: {
        ...this.defaultOptions.headers,
        'Accept': this.getMimeType(format)
      }
    }).then(response => response.data);
  }

  async importData(endpoint: string, file: File): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.httpClient.postApi<any>(`${endpoint}/import`, formData, {
      ...this.defaultOptions,
      headers: {
        ...this.defaultOptions.headers,
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  // ==================== FILE OPERATIONS ====================

  async uploadFile(endpoint: string, file: File, metadata?: any): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (metadata) {
      formData.append('metadata', JSON.stringify(metadata));
    }

    return this.httpClient.postApi<any>(endpoint, formData, {
      ...this.defaultOptions,
      headers: {
        ...this.defaultOptions.headers,
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        this.emitUploadProgress(endpoint, percentCompleted);
      }
    });
  }

  async downloadFile(endpoint: string, fileId: ID): Promise<Blob> {
    const url = `${endpoint}/${fileId}/download`;
    
    return this.httpClient.get<Blob>(url, {
      ...this.defaultOptions,
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        this.emitDownloadProgress(endpoint, fileId, percentCompleted);
      }
    }).then(response => response.data);
  }

  // ==================== UTILITY METHODS ====================

  setAuthToken(token: string): void {
    this.httpClient.setAuthToken(token);
    this.defaultOptions.headers = {
      ...this.defaultOptions.headers,
      'Authorization': `Bearer ${token}`
    };
  }

  clearAuth(): void {
    this.httpClient.clearAuthToken();
    delete this.defaultOptions.headers?.['Authorization'];
    
    // Clear all cached auth-related data
    this.httpClient.clearAllCache();
  }

  clearCache(cacheKey: string | RegExp): void {
    if (typeof cacheKey === 'string') {
      // String ise hem kendi cache'imden hem de httpClient cache'inden temizle
      this.cache.delete(cacheKey);
      this.httpClient.clearCache(cacheKey);
    } else {
      // RegExp ise kendi cache'imden pattern'e göre temizle
      const keysToDelete: string[] = [];
      for (const key of this.cache.keys()) {
        if (cacheKey.test(key)) {
          keysToDelete.push(key);
        }
      }
      for (const key of keysToDelete) {
        this.cache.delete(key);
      }
      
      // HttpClient cache'ini tamamen temizle (pattern desteklemiyor)
      this.httpClient.clearAllCache();
    }
  }
  
  getCacheKeys(): string[] {
    return Array.from(this.cache.keys());
  }
  
  getCache<T>(key: string): T | null {
    const item = this.cache.get(key);
    if (!item || Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }
    return item.data as T;
  }
  
  setCache<T>(key: string, value: T, ttl: number = 300000): void {
    const expiry = Date.now() + ttl;
    this.cache.set(key, { data: value, expiry });
  }
  
  // HTTP isteklerinde cache kullanımı
  async customGet<T>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    const cacheKey = options?.cacheKey || endpoint;
    
    // Cache kontrolü
    if (options?.skipCache !== true) {
      const cached = this.getCache<T>(cacheKey);
      if (cached !== null) {
        return {
          data: cached,
          message: 'Cached response',
          status: 200,
          success: true,
          timestamp: new Date().toISOString()
        };
      }
    }
    
    // Cache'te yoksa API'den al
    const response = await this.httpClient.getApi<T>(endpoint, {
      ...this.defaultOptions,
      ...options
    });
    
    // Başarılıysa cache'e kaydet
    if (response.success && options?.cacheTTL !== undefined) {
      this.setCache(cacheKey, response.data, options.cacheTTL);
    }
    
    return response;
  }

  setBaseURL(baseURL: string): void {
    this.baseURL = baseURL;
    this.httpClient.setBaseURL(baseURL);
  }

  cancelRequest(cancelKey: string): void {
    // Bu implementasyon axiosClient'taki cancel mekanizmasına bağlı
    // Daha gelişmiş bir cancel manager eklenebilir
    this.httpClient.cancelRequest(`Cancelled by key: ${cancelKey}`);
  }

  cancelAllRequests(): void {
    this.httpClient.cancelAllRequests();
  }

  // ==================== PRIVATE METHODS ====================

  private wrapResponse<T>(response: HttpResponse): ApiResponse<T> {
    if (isApiResponse<T>(response.data)) {
      return response.data;
    }

    return {
      data: response.data as T,
      message: 'Success',
      status: response.status,
      success: response.status >= 200 && response.status < 300,
      timestamp: new Date().toISOString()
    };
  }

  private extractPaginationMeta(response: HttpResponse): any {
    const headers = response.headers;
    
    return {
      total: Number.parseInt(headers['x-total-count'] || '0'),
      page: Number.parseInt(headers['x-page'] || '1'),
      limit: Number.parseInt(headers['x-limit'] || '10'),
      totalPages: Number.parseInt(headers['x-total-pages'] || '1'),
      hasNext: headers['x-has-next'] === 'true',
      hasPrev: headers['x-has-prev'] === 'true'
    };
  }

  private getMimeType(format: ExportFormat): string {
    const mimeTypes = {
      'csv': 'text/csv',
      'excel': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'pdf': 'application/pdf',
      'json': 'application/json'
    };
    
    return mimeTypes[format] || 'application/octet-stream';
  }

  private emitUploadProgress(endpoint: string, progress: number): void {
    const event = new CustomEvent('api-upload-progress', {
      detail: {
        endpoint,
        progress,
        timestamp: Date.now()
      }
    });
    globalThis.dispatchEvent(event);
  }

  private emitDownloadProgress(endpoint: string, fileId: ID, progress: number): void {
    const event = new CustomEvent('api-download-progress', {
      detail: {
        endpoint,
        fileId,
        progress,
        timestamp: Date.now()
      }
    });
    globalThis.dispatchEvent(event);
  }

  // ==================== BUILDER PATTERN ====================

  /**
   * Request builder for fluent API
   */
  request() {
    return new RequestBuilder(this);
  }

  /**
   * Query builder for GET requests
   */
  query<T = any>(endpoint: string): QueryBuilder<T> {
    return new QueryBuilder<T>(this, endpoint);
  }

  /**
   * Set default header for all requests
   */
  setDefaultHeader(key: string, value: string): void {
    this.defaultOptions.headers = {
      ...this.defaultOptions.headers,
      [key]: value
    };
    this.httpClient.setDefaultHeader(key, value);
  }

  /**
   * Remove default header
   */
  removeDefaultHeader(key: string): void {
    if (this.defaultOptions.headers && key in this.defaultOptions.headers) {
      const { [key]: removed, ...rest } = this.defaultOptions.headers;
      this.defaultOptions.headers = rest;
    }
    this.httpClient.removeDefaultHeader(key);
  }

  /**
   * Set request timeout
   */
  setTimeout(timeout: number): void {
    this.defaultOptions.timeout = timeout;
    this.httpClient.setTimeout(timeout);
  }

  /**
   * Set retry configuration
   */
  setRetryConfig(count: number, delay: number): void {
    this.defaultOptions.retryCount = count;
    this.defaultOptions.retryDelay = delay;
    this.httpClient.setRetryConfig(count, delay);
  }

  // ==================== IApiClient INTERFACE'DEN EKSİK METODLAR ====================

  // Bunlar zaten var:
  // setAuthToken(token: string): void ✓
  // clearAuth(): void ✓
  // setBaseURL(baseURL: string): void ✓
  // cancelRequest(cancelKey: string): void ✓
  // cancelAllRequests(): void ✓

  // Eksik olanlar:
  
  /**
   * Clear all default headers
   */
  clearDefaultHeaders(): void {
    this.defaultOptions.headers = {};
    this.httpClient.clearDefaultHeaders();
  }

  /**
   * Enable/disable cache
   */
  setCacheEnabled(enabled: boolean): void {
    this.httpClient.setCacheEnabled(enabled);
  }

  /**
   * Check if online
   */
  isOnline(): boolean {
    return this.httpClient.isOnline();
  }
}

/**
 * Request Builder for fluent API
 */
class RequestBuilder {
  private readonly client: ApiClient;
  private config: RequestOptions = {};
  private endpoint: string = '';
  private method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET';
  private data?: any;
  private params?: any;

  constructor(client: ApiClient) {
    this.client = client;
  }

  to(endpoint: string): this {
    this.endpoint = endpoint;
    return this;
  }

  withMethod(method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'): this {
    this.method = method;
    return this;
  }

  withData(data: any): this {
    this.data = data;
    return this;
  }

  withParams(params: any): this {
    this.params = params;
    return this;
  }

  withOptions(options: RequestOptions): this {
    this.config = { ...this.config, ...options };
    return this;
  }

  cache(key: string, ttl?: number): this {
    this.config.cacheKey = key;
    if (ttl) this.config.cacheTTL = ttl;
    return this;
  }

  skipAuth(): this {
    this.config.requiresAuth = false;
    return this;
  }

  async execute<T>(): Promise<ApiResponse<T>> {
    if (!this.endpoint) {
      throw new Error('Endpoint is required');
    }

    switch (this.method) {
      case 'GET':
        return this.client.customGet<T>(this.endpoint, {
          ...this.config,
          params: this.params
        });
      case 'POST':
        return this.client.customPost<T>(this.endpoint, this.data, {
          ...this.config,
          params: this.params
        });
      case 'PUT':
        return this.client.customPut<T>(this.endpoint, this.data, this.config);
      case 'PATCH':
        return this.client.customPatch<T>(this.endpoint, this.data, this.config);
      case 'DELETE':
        return this.client.customDelete<T>(this.endpoint, this.config);
      default:
        throw new Error(`Unsupported method: ${this.method}`);
    }
  }
}

/**
 * Query Builder for GET requests
 */
class QueryBuilder<T> {
  private readonly client: ApiClient;
  private readonly endpoint: string;
  private params: BaseQueryParams = {};
  private readonly options: RequestOptions = {};

  constructor(client: ApiClient, endpoint: string) {
    this.client = client;
    this.endpoint = endpoint;
  }

  page(page: number): this {
    this.params.page = page;
    return this;
  }

  limit(limit: number): this {
    this.params.limit = limit;
    return this;
  }

  sortBy(field: string, order: 'asc' | 'desc' = 'asc'): this {
    this.params.sortBy = field;
    this.params.sortOrder = order;
    return this;
  }

  search(query: string): this {
    this.params.search = query;
    return this;
  }

  filter(filters: Record<string, any>): this {
    this.params = { ...this.params, ...filters };
    return this;
  }

  cache(key: string, ttl?: number): this {
    this.options.cacheKey = key;
    if (ttl) this.options.cacheTTL = ttl;
    return this;
  }

  async get(): Promise<ApiResponse<T>> {
    return this.client.customGet<T>(this.endpoint, {
      ...this.options,
      params: this.params
    });
  }

  async list(): Promise<PaginatedResponse<T>> {
    return this.client.getList<T>(this.endpoint, this.params, this.options);
  }
}

// Singleton instance
export const apiClient = new ApiClient();

// Default export
export default apiClient;